import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  AudioLines,
  Bell,
  CalendarClock,
  CheckSquare,
  Hash,
  ListMusic,
  Loader2,
  Megaphone,
  Send,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Stepper,
  useStepper,
  type StepDescriptor,
} from '@/components/ui/stepper';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import {
  templateFeatures,
  type TtsFlowFormValues,
} from '../types';
import { StepBasics } from './steps/StepBasics';
import { StepTtsRows } from './steps/StepTtsRows';
import { StepVoiceDtmf } from './steps/StepVoiceDtmf';
import { StepPatch } from './steps/StepPatch';
import { StepNotifSchedule } from './steps/StepNotifSchedule';
import { StepReview } from './steps/StepReview';

interface Props {
  mode: 'create' | 'update' | 'clone';
  initialValues: TtsFlowFormValues;
  isPending: boolean;
  onSubmit: (values: TtsFlowFormValues) => void;
  /** Where to send the user when they click Back from the header. */
  backHref?: string;
}

const ALL_STEPS = [
  { id: 'basics', label: 'Basics', icon: Megaphone },
  { id: 'rows', label: 'TTS rows', icon: ListMusic },
  { id: 'voiceDtmf', label: 'Voice & DTMF', icon: AudioLines },
  { id: 'patch', label: 'Patch', icon: Users },
  { id: 'notifSchedule', label: 'Notify & Schedule', icon: Bell },
  { id: 'review', label: 'Review', icon: CheckSquare },
] satisfies (StepDescriptor & { id: string })[];

type StepId = (typeof ALL_STEPS)[number]['id'];

/** Which steps apply for a given template. Template 7 (TTS Simple) skips
 *  the DTMF/voice-files step and the patch step. Template 8 skips patch. */
function visibleSteps(t: TtsFlowFormValues['templateId']): StepDescriptor[] {
  const f = templateFeatures(t);
  return ALL_STEPS.filter((s) => {
    if (s.id === 'voiceDtmf') return f.hasVoiceFiles;
    if (s.id === 'patch') return f.hasPatch;
    return true;
  });
}

function validateStepById(
  id: StepId,
  v: TtsFlowFormValues,
): string | null {
  const f = templateFeatures(v.templateId);

  if (id === 'basics') {
    if (!v.templateId) return 'Pick a template.';
    if (!v.ttsFlowName.trim()) return 'Give the flow a name.';
    if (!v.language) return 'Pick a language.';
    if (!v.gender) return 'Pick a voice gender.';
    return null;
  }

  if (id === 'rows') {
    if (v.ttsRows.length === 0)
      return 'Add at least one step — prompt, text, or variable.';
    for (let i = 0; i < v.ttsRows.length; i++) {
      const r = v.ttsRows[i]!;
      if (r.ttsType === 'variable') {
        if (!r.value) return `Step ${i + 1}: pick a variable slot.`;
        if (!r.variableType) return `Step ${i + 1}: pick a variable type.`;
        continue;
      }
      if (r.text !== undefined) {
        if (!r.text.trim()) return `Step ${i + 1}: type the text to speak.`;
        continue;
      }
      if (!r.value) return `Step ${i + 1}: pick a prompt or switch to Text.`;
    }
    return null;
  }

  if (id === 'voiceDtmf') {
    if (!f.hasVoiceFiles) return null;
    if (!v.menuId) return 'Pick a menu voice file.';
    if (!v.menuWaitTime) return 'Pick a menu wait time.';
    if (!v.rePrompt) return 'Pick a re-prompt count.';
    if (v.templateId === '8' && v.dtmf.length === 0)
      return 'Pick at least one DTMF digit.';
    if (f.needsNoAgent && !v.noAgentId)
      return 'Pick a "no-agent" voice file for Call Patch flows.';
    return null;
  }

  if (id === 'patch') {
    if (!f.hasPatch) return null;
    if (v.patchList.length === 0)
      return 'Bind at least one DTMF digit to an agent group.';
    return null;
  }

  if (id === 'notifSchedule') {
    if (!v.startDate) return 'Pick a start date.';
    if (!v.endDate) return 'Pick an end date.';
    if (!v.startTime) return 'Pick a daily start time.';
    if (!v.endTime) return 'Pick a daily end time.';
    if (v.startDate > v.endDate)
      return 'Start date cannot be after the end date.';
    return null;
  }

  return null;
}

export function TtsFlowComposeWizard({
  mode,
  initialValues,
  isPending,
  onSubmit,
  backHref = '/tts-flow-list',
}: Props) {
  const [values, setValuesRaw] = useState<TtsFlowFormValues>(initialValues);
  const setValues = (patch: Partial<TtsFlowFormValues>) =>
    setValuesRaw((prev) => ({ ...prev, ...patch }));

  const steps = useMemo(() => visibleSteps(values.templateId), [
    values.templateId,
  ]);
  const stepper = useStepper(steps.length);
  const currentId = (steps[stepper.current]?.id ?? 'basics') as StepId;

  const handleNext = () => {
    const err = validateStepById(currentId, values);
    if (err) return toast.error(err);
    stepper.next();
  };

  const handleSubmit = () => {
    // Re-run every prior step's validator before submitting so jumping back
    // and forward via the stepper can't skip a check.
    for (let i = 0; i < steps.length - 1; i++) {
      const step = steps[i];
      if (!step) continue;
      const err = validateStepById(step.id as StepId, values);
      if (err) {
        toast.error(err);
        stepper.goTo(i);
        return;
      }
    }
    onSubmit(values);
  };

  const stepBody = useMemo(() => {
    switch (currentId) {
      case 'basics':
        return (
          <StepBasics
            values={values}
            setValues={setValues}
            lockTemplate={mode === 'update'}
            autoFocusName={mode === 'create'}
          />
        );
      case 'rows':
        return <StepTtsRows values={values} setValues={setValues} />;
      case 'voiceDtmf':
        return <StepVoiceDtmf values={values} setValues={setValues} />;
      case 'patch':
        return <StepPatch values={values} setValues={setValues} />;
      case 'notifSchedule':
        return <StepNotifSchedule values={values} setValues={setValues} />;
      case 'review':
        return <StepReview values={values} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, values, mode]);

  return (
    <ScrollPage>
      <PageHeader
        title={
          mode === 'create'
            ? 'New TTS Flow'
            : mode === 'clone'
              ? 'Clone TTS Flow'
              : 'Edit TTS Flow'
        }
        description="Build a text-to-speech automation: pick prompts/variables, optionally collect DTMF and patch to agents, then schedule."
        actions={
          <Button asChild variant="outline">
            <Link to={backHref}>
              <ArrowLeft className="h-4 w-4" />
              Back to TTS flows
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={steps}
        current={stepper.current}
        completed={stepper.completed}
        onStepClick={stepper.goTo}
      />

      <div className="mt-6 space-y-4">{stepBody}</div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={stepper.prev}
          disabled={stepper.isFirst || isPending}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {stepper.isLast ? (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {mode === 'create' || mode === 'clone'
              ? 'Save TTS flow'
              : 'Save changes'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={isPending}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ScrollPage>
  );
}

// Suppress unused-import warning — kept for menu-icon parity with other wizards.
void Hash;
void CalendarClock;
